// @flow
import { NUMBER_WITH_COMMAS } from '../../application/constants';
import {
  transform,
  pick,
  keys as getKeys,
  isEqual,
  toString,
  isNumber,
  compact,
  mapValues,
  trim,
} from 'lodash';

export type TranslatedOption = {
  label: string,
  value: string,
};

export function deepMapKeys(obj: Object, mapFunc: Function) {
  return transform(obj, (r, v, k) => {
    const value = v instanceof Object ? deepMapKeys(v, mapFunc) : v;
    r[mapFunc(k)] = value;
  });
}

export const translatedValue = (t: Function, selectedValue: Object) =>
  selectedValue
    ? { value: selectedValue.value, label: t(selectedValue.label) }
    : selectedValue;

export function translatedOptions(
  t: (string, ?Object) => string,
  options: Array<Object>,
): Array<TranslatedOption> {
  return options.map(type => ({ label: t(type.label), value: type.value }));
}

export function addCommas(value: number | string): string {
  const stringFormat = `${value}`;
  const x = stringFormat.split('.');
  let x1 = x[0];
  const x2 = x.length > 1 ? `.${x[1]}` : '';
  const regex = /(\d+)(\d{3})/;
  while (regex.test(x1)) {
    x1 = x1.replace(regex, '$1,$2');
  }
  return x1 + x2;
}

export function trimCommas(value: string): string {
  return trim(NUMBER_WITH_COMMAS.test(value) ? value.replace(/,/g, '') : value);
}

export function removeNonNumeric(num: number | string): string {
  return num.toString().replace(/[^0-9]/g, '');
}

export function getErrorMessage(
  t: (string, any) => void,
  value: string,
): ?string {
  const message = typeof value === 'string' ? value.match(/\D+/g) : '';
  const attributes =
    (message &&
      message
        .pop()
        .trim()
        .split('_')) ||
    [];
  const code = typeof value === 'string' ? value.match(/\d+/g) : '';
  if (code) {
    const m = t(`errors.${code[0]}`);
    if (m == null || m.split('.')[0] === 'errors') {
      return value;
    }

    return t(`errors.${code[0]}`, {
      attr: t(`attributes.${attributes.pop()}`),
      secondAttr:
        (isNaN(code[1]) ? code[1] : addCommas(code[1])) ||
        t(`attributes.${attributes.pop()}`),
    });
  }

  return attributes.pop();
}

export const filterValues = (values: Object, defaltValue: Object) =>
  pick(values, getKeys(defaltValue));

export function transformDate(str: string): string {
  const regex = /^(\d{4})\/(0?[1-9]|1[012])\/(0?[1-9]|[12][0-9]|3[01])$/;
  if (regex.test(str)) {
    const parts = compact(str.split(regex));
    return `${parts[0]}-${`0${parts[1]}`.slice(-2)}-${`0${parts[2]}`.slice(
      -2,
    )}`;
  }
  return str;
}

export function transformDeepObject(obj: Object) {
  return transform(obj, (r, v, k) => {
    const value = v instanceof Object ? transformDeepObject(v) : v;
    r[k] = isNumber(value) || !value ? toString(value) : transformDate(value);
  });
}

export function isEqualObject(obj1: Object, obj2: Object): boolean {
  return isEqual(transformDeepObject(obj1), transformDeepObject(obj2));
}

export function briefNumberFormatter(num: number): string {
  if (Math.abs(num) > 999999999999) {
    return num.toPrecision(2);
  }
  return num.toLocaleString();
}

export function roundNumber(arr: Array<Object>): Array<Object> {
  return arr.map(o => mapValues(o, v => (isNumber(v) ? Math.round(v) : v)));
}

export function delimited(n: ?number): string {
  if (n == null) {
    return '';
  }

  return String(n).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
}

export function numberToCurrency(n: ?number): string {
  if (n == null) {
    return '0円';
  }

  return delimited(n) + '円';
}

export function isNumberString(s: string): boolean {
  return /([0-9]+|Infinity)$/.test(s);
}
